import { FormikContextType, FormikValues } from 'formik';
import { format, addHours, parse } from 'date-fns';
import { ISigningAppointmentResponse } from 'types/signingAppointment/ISigningAppointmentResponse';
import { DssAppointmentOption } from '../../../../types/signingAppointment/CreateSigningAppointmentRequest';
import { IOrderDetailsData } from '../../../../ui-toolkit/orders/interfaces/IOrderDetailsData';

export const DSS_INITIAL_VALUES = {
  signerWillProvideLocation: false,
  signerSelectsFromRange: false,
  isDssDateRange: false,
  dssDate: '',
  dssStartTime: '08:00:00',
  dssEndTime: '15:00:00',
  dssDateRangeStart: '',
  dssDateRangeEnd: '',
};

export const sameAsPropertyAddress = (values: FormikValues, order: IOrderDetailsData): boolean => {
  return (
    !!values.address &&
    values.address.streetAddress === order.property.street_address &&
    values.address.unit === order.property.unit &&
    values.address.city === order.property.city &&
    values.address.stateAbbreviation === order.property.state &&
    values.address.zipCode === order.property.zip_code
  );
};

export const onChangeSameAsPropertyAddress = (formik: FormikContextType<any>, order: IOrderDetailsData) => {
  if (!sameAsPropertyAddress(formik.values, order)) {
    formik.setFieldTouched('address.stateAbbreviation');
    formik.setValues({
      ...formik.values,
      address: {
        id: formik.values.address?.id,
        streetAddress: order.property.street_address,
        unit: order.property.unit,
        city: order.property.city,
        stateAbbreviation: order.property.state,
        zipCode: order.property.zip_code,
      },
    });
  }
};

export const formatEndTime = (time: string) => format(addHours(parse(time, 'HH:mm:ss', new Date()), 1), 'HH:mm:ss');

export const extractDssValues = (appointment: ISigningAppointmentResponse | undefined) => {
  if (!appointment) {
    return DSS_INITIAL_VALUES;
  }

  const { dssAppointmentOptions } = appointment;

  if (dssAppointmentOptions?.length) {
    return extractValuesForAppointmentOptions(appointment, dssAppointmentOptions);
  }

  return {
    ...DSS_INITIAL_VALUES,
    signerWillProvideLocation: !appointment.address,
  };
};

const extractValuesForAppointmentOptions = (
  appointment: ISigningAppointmentResponse,
  dssAppointmentOptions: DssAppointmentOption[]
) => {
  const isDateRange = new Set(dssAppointmentOptions.map(({ appointmentDate }) => appointmentDate)).size > 1;

  return {
    ...DSS_INITIAL_VALUES,
    dssStartTime: dssAppointmentOptions[0].appointmentTime,
    dssEndTime: formatEndTime(dssAppointmentOptions[dssAppointmentOptions.length - 1].appointmentTime as string),
    signerSelectsFromRange: true,
    signerWillProvideLocation: !appointment.address,
    isDssDateRange: isDateRange,
    dssDateRangeStart: isDateRange ? dssAppointmentOptions[0].appointmentDate : '',
    dssDateRangeEnd: isDateRange ? dssAppointmentOptions[dssAppointmentOptions.length - 1].appointmentDate : '',
    dssDate: isDateRange ? '' : dssAppointmentOptions[0].appointmentDate,
  };
};

export const determineCanEdit = (appointment: ISigningAppointmentResponse) => {
  const { isSignatureSyncAppointment, isDigitalScheduling, isDigitalSchedulingDraft } = appointment;
  return !isSignatureSyncAppointment && !(isDigitalScheduling && !isDigitalSchedulingDraft);
};
